import { Card, SectionHeader, requiredField } from "common/transaction_creation/v3/common";
import {
  type SectionContract,
  type SectionComponentProps,
} from "common/transaction_creation/v3/form";
import EnoteSection from "common/mortgage/transactions/edit/sub_forms/enote_section";
import { LENDER_HYBRID_TRANSACTION_TYPES } from "constants/transaction";
import { isFeatureEnabled } from "util/feature_detection";
import { ENABLE_ENOTES_IN_HYBRIDS } from "constants/feature_gates";
import { segmentTrack } from "util/segment";
import { EVENT } from "constants/analytics";
import { useRawMutation } from "util/graphql/mutation";
import type { SmartDocsLoans } from "graphql_globals";
import UpdatePaperNoteConsentMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/update_paper_note_consent_mutation.graphql";
import AddEnoteToTransactionMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/add_enote_to_transaction_mutation.graphql";
import RemoveDocumentFromTransactionMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/remove_document_from_transaction_mutation.graphql";
import UpdateMortgageBorrowerMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/update_mortgage_borrower_mutation.graphql";
import RemoveEnoteSeedMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/remove_enote_seed_mutation.graphql";
import SaveEnoteSeedMutation from "common/mortgage/transactions/edit/sub_forms/enote_section/save_enote_seed_mutation.graphql";
import type { parseFormValuesToEnoteSeedFields } from "util/enote_seed";

import type { PromissoryNote } from "./transaction_fragment.graphql";
import type { PromissoryNoteOrg } from "./organization_fragment.graphql";

const CONFIGS = {
  promissoryNote: "promissoryNote",
} as const;

const noopPromise = () => {
  return Promise.resolve({});
};

function PromissoryNoteSection({
  transaction,
  organization,
  config,
}: SectionComponentProps<PromissoryNote, PromissoryNoteOrg, unknown>) {
  const documents = transaction.documentBundle?.documents;

  const enoteDocument = documents?.edges.find((document) => {
    return document.node.is_enote;
  });

  const enoteSeed = transaction.documentBundle?.enoteSeed || undefined;
  const SUPPRESS_OPTIONS_ANALYTICS = { suppressAnalytics: true };

  const [updatePaperNoteConsentMutateFn, { loading: paperNoteConsentLoading }] = useRawMutation(
    UpdatePaperNoteConsentMutation,
    {},
    SUPPRESS_OPTIONS_ANALYTICS,
  );

  const [removeDocumentFromTransactionMutateFn, { loading: removeDocumentFromTransactionLoading }] =
    useRawMutation(RemoveDocumentFromTransactionMutation);

  const [addEnoteToTransactionMutateFn, { loading: addEnoteToTransactionLoading }] = useRawMutation(
    AddEnoteToTransactionMutation,
    {},
    SUPPRESS_OPTIONS_ANALYTICS,
  );

  const [updateMortgageBorrowerMutateFn, { loading: updateMortgageBorrowerLoading }] =
    useRawMutation(UpdateMortgageBorrowerMutation);

  const [removeEnoteSeedMutateFn, { loading: removeEnoteSeedLoading }] =
    useRawMutation(RemoveEnoteSeedMutation);
  const [saveEnoteSeedMutateFn, { loading: saveEnoteSeedLoading }] =
    useRawMutation(SaveEnoteSeedMutation);

  const enoteMutationLoading =
    paperNoteConsentLoading ||
    addEnoteToTransactionLoading ||
    removeDocumentFromTransactionLoading ||
    updateMortgageBorrowerLoading ||
    removeEnoteSeedLoading ||
    saveEnoteSeedLoading;

  function onUpdatePaperNoteConsent(paperNoteExists: boolean): void {
    updatePaperNoteConsentMutateFn({
      variables: {
        input: {
          transactionId: transaction.id,
          paperNoteConsent: paperNoteExists,
        },
      },
    }).then(() => {
      segmentTrack(EVENT.UPDATE_PAPER_NOTE_CONSENT, {
        paper_note_consent: paperNoteExists,
      });
    });
    // .catch(this.handleMutationError); TODO: REAL-9445 - error handling
  }

  function onAddEnoteToTransaction(s3FileHandle: string) {
    return addEnoteToTransactionMutateFn({
      variables: {
        input: {
          organizationTransactionId: transaction.id,
          fileHandle: s3FileHandle,
          organizationId: organization.id,
        },
      },
    });
    // TODO: REAL-9454 - error handling
  }

  function onRemoveDocumentFromTransaction(documentId: string): void {
    removeDocumentFromTransactionMutateFn({
      variables: {
        input: {
          organizationTransactionId: transaction.id,
          documentId,
        },
      },
    });
    // .catch(this.handleMutationError); TODO: REAL-9454 - error handling
  }

  function onUpdateMortgageBorrower(id: string, ssn?: string): void {
    updateMortgageBorrowerMutateFn({
      variables: {
        input: {
          id,
          ssn,
        },
      },
    });
    // .catch(this.handleMutationError); TODO: REAL-9454 - error handling
  }

  function onRemoveEnoteSeed(): void {
    const documentBundleId = transaction.documentBundle?.id;

    if (documentBundleId) {
      removeEnoteSeedMutateFn({
        variables: {
          input: {
            documentBundleId,
          },
        },
      });
    }
    // .catch(this.handleMutationError); TODO: REAL-9454 - error handling
  }

  function onSaveEnoteSeed(
    enoteSeedFields: ReturnType<typeof parseFormValuesToEnoteSeedFields>,
    finalize = false,
  ): Promise<unknown> {
    const documentBundleId = transaction.documentBundle?.id;

    if (documentBundleId) {
      return saveEnoteSeedMutateFn({
        variables: {
          input: {
            documentBundleId,
            enoteSeedFields,
            finalize,
          },
        },
      });
    }
    throw new Error("eNoteSeed required");
    // TODO: REAL-9454 - error handling
  }

  function onInitializeEnoteSeed(smartDocType: SmartDocsLoans): Promise<unknown> {
    return addEnoteToTransactionMutateFn({
      variables: {
        input: {
          organizationTransactionId: transaction.id,
          smartDocType,
          organizationId: organization.id,
        },
      },
    });
    // .catch(this.handleMutationError); TODO: REAL-9454 - error handling
  }

  return (
    <>
      <SectionHeader iconName="blank-doc">Promissory note</SectionHeader>

      <Card>
        <EnoteSection
          onUpdatePaperNoteConsent={onUpdatePaperNoteConsent}
          paperNoteConsent={Boolean(transaction.paperNoteConsent)}
          enoteMutationLoading={enoteMutationLoading}
          onAddEnoteToTransaction={onAddEnoteToTransaction}
          onRemoveDocumentFromTransaction={onRemoveDocumentFromTransaction}
          onUpdateMortgageBorrower={onUpdateMortgageBorrower}
          onRemoveEnoteSeed={onRemoveEnoteSeed}
          onSaveEnoteSeed={onSaveEnoteSeed}
          onInitializeEnoteSeed={onInitializeEnoteSeed}
          enoteDocument={enoteDocument?.node}
          enoteWarning={null} // TODO: REAL-9454 error handling
          enoteSeed={enoteSeed}
          checkCanAddEnote={noopPromise} // used in v2 txn form validation - noop bc of v3 txn form validation
          isRequired={requiredField(config, CONFIGS.promissoryNote)}
        />
      </Card>
    </>
  );
}

export const PROMISSORY_NOTE_SECTION = {
  Component: PromissoryNoteSection,
  configs: CONFIGS,
  modifyConfig({ sectionConfig, transaction }) {
    const modifiedConfig = { ...sectionConfig };

    if (
      transaction.transactionType &&
      Object.values(LENDER_HYBRID_TRANSACTION_TYPES).includes(transaction.transactionType) &&
      isFeatureEnabled(transaction.organization, ENABLE_ENOTES_IN_HYBRIDS)
    ) {
      modifiedConfig.promissoryNote.display = "editable";
    }

    return modifiedConfig;
  },
} satisfies SectionContract<
  Record<never, never>,
  Record<never, never>,
  PromissoryNote,
  PromissoryNoteOrg,
  unknown,
  typeof CONFIGS
>;
